import { useParams } from 'react-router-dom';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type { MovementDetail } from '../../lib/types/inventory.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

const MOVEMENT_TITLES: Record<string, string> = {
  receipt: 'برگه رسمی رسید انبار (ورود کالا)',
  issue: 'برگه رسمی حواله خروج انبار (خروج کالا)',
  transfer: 'برگه رسمی حواله انتقال بین انبارها',
  count_adjustment: 'صورتجلسه رسمی تعدیل انبارگردانی',
};

/**
 * Official Warehouse printable document (سند رسمی انبار — رسید / حواله / انتقال / تعدیل).
 * Conforms to standard Iranian inventory and accounting procedures:
 * - Issuing legal entity profile (شناسه ملی، کد اقتصادی، آدرس)
 * - Movement metadata (شماره سریال سند، تاریخ سند، نوع عملیات، مرجع/سفارش)
 * - Counterparty profile (تأمین‌کننده در رسید، تحویل‌گیرنده/مشتری در خروج)
 * - Itemized inventory table (کد کالا، شرح، واحد، مقدار، فی، مبلغ کل، بچ/سریال، محل انبار)
 * - Totals summary row
 * - 4-step formal authorized legal signatures box according to internal controls.
 */
export function MovementPrintPage() {
  const { slug, movementId } = useParams<{ slug: string; movementId: string }>();
  const { unit, label, ready } = useAmountDisplay();
  const base = slug ? tenantBasePath(slug) : '';

  const movement = useApiGet<MovementDetail>(
    slug && movementId ? `${base}/api/v1/inventory/movements/${movementId}` : null,
    [slug, movementId],
  );
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);

  if (!slug || !movementId) return null;
  const backHref = `${base}/inventory/movements/${movementId}`;

  if (!ready) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به سند انبار">
        <PrintLoadState />
      </PrintPageShell>
    );
  }

  if (!isReady(movement)) {
    return (
      <PrintPageShell backHref={backHref} backLabel="بازگشت به سند انبار">
        <PrintLoadState error={movement.status === 'error' ? movement.error : undefined} />
      </PrintPageShell>
    );
  }

  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;

  const doc = movement.data;
  const docTitle = MOVEMENT_TITLES[doc.movementType] ?? 'سند رسمی انبار';
  const lines = doc.lines ?? [];

  const totalQty = lines.reduce(
    (sum, l) => sum + Number(l.quantityNumerator) / Number(l.quantityDenominator || 1),
    0,
  );

  const totalValue = doc.financeHandoff?.totalValueRial
    ? BigInt(doc.financeHandoff.totalValueRial)
    : lines.reduce((sum, l) => {
        const qty = Number(l.quantityNumerator) / Number(l.quantityDenominator || 1);
        const unitCost = l.unitCostRial ? BigInt(l.unitCostRial) : 0n;
        return sum + BigInt(Math.round(qty)) * unitCost;
      }, 0n);

  return (
    <PrintPageShell backHref={backHref} backLabel="بازگشت به سند انبار">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 12 }}>
        {/* Top Header: Company Identity & Voucher Metadata */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'flex-end',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>{docTitle}</span>
            <span
              className="ltr sk-num"
              style={{ fontFamily: 'var(--sk-font-mono)', fontWeight: 600 }}
            >
              شماره سند: {doc.movementNumber}
            </span>
            <span>تاریخ سند: {displayJalaliDate(doc.businessDate)}</span>
            {doc.sourceReference ? (
              <span
                className="ltr sk-num"
                style={{ fontFamily: 'var(--sk-font-mono)', color: '#444' }}
              >
                شماره مرجع / سفارش: {doc.sourceReference}
              </span>
            ) : null}
            <span style={{ fontSize: 11, color: '#444' }}>
              وضعیت: <b>قطعی (ثبت‌شده در کاردکس)</b>
            </span>
          </div>
        </div>

        {/* Counterparty & Warehouse Info Box */}
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: 6,
            padding: '10px 14px',
            backgroundColor: '#fcfcfc',
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          <div>
            <span style={{ color: '#666', fontSize: 11, display: 'block' }}>
              {doc.movementType === 'receipt'
                ? 'تأمین‌کننده / فروشنده کالا:'
                : doc.movementType === 'issue'
                  ? 'تحویل‌گیرنده / مشتری کالا:'
                  : 'طرف‌حساب سند:'}
            </span>
            <b style={{ fontSize: 13 }}>
              {doc.partyName ? `${doc.partyName} (${doc.partyCode})` : (doc.partyCode ?? '—')}
            </b>
          </div>

          <div>
            <span style={{ color: '#666', fontSize: 11, display: 'block' }}>
              انبار مبدأ / عملیات:
            </span>
            <b>{lines[0]?.warehouseName ?? lines[0]?.warehouseCode ?? 'انبار مرکزی'}</b>
          </div>

          <div>
            <span style={{ color: '#666', fontSize: 11, display: 'block' }}>
              {doc.movementType === 'transfer' ? 'انبار مقصد:' : 'شماره عطف سیستمی:'}
            </span>
            <b>
              {doc.movementType === 'transfer'
                ? (lines[0]?.counterWarehouseName ?? lines[0]?.counterWarehouseCode ?? '—')
                : (doc.sourceReference ?? doc.movementId.slice(0, 8))}
            </b>
          </div>
        </div>

        {/* Line Items Table */}
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 11,
              border: '1px solid #999',
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '1px solid #999' }}>
                <th
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #bbb',
                    width: 36,
                    textAlign: 'center',
                  }}
                >
                  #
                </th>
                <th style={{ padding: '6px 8px', border: '1px solid #bbb', minWidth: 100 }}>
                  کد کالا
                </th>
                <th style={{ padding: '6px 8px', border: '1px solid #bbb', minWidth: 160 }}>
                  شرح کالا
                </th>
                <th
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #bbb',
                    width: 70,
                    textAlign: 'center',
                  }}
                >
                  واحد
                </th>
                <th
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #bbb',
                    width: 80,
                    textAlign: 'center',
                  }}
                >
                  مقدار
                </th>
                <th
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #bbb',
                    width: 100,
                    textAlign: 'left',
                  }}
                >
                  بهای واحد ({label})
                </th>
                <th
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #bbb',
                    width: 110,
                    textAlign: 'left',
                  }}
                >
                  مبلغ کل ({label})
                </th>
                <th style={{ padding: '6px 8px', border: '1px solid #bbb', width: 90 }}>
                  بچ / سریال
                </th>
                <th style={{ padding: '6px 8px', border: '1px solid #bbb', width: 100 }}>
                  موقعیت قفسه
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const qty = Number(l.quantityNumerator) / Number(l.quantityDenominator || 1);
                const unitPrice = l.unitCostRial ? BigInt(l.unitCostRial) : 0n;
                const lineTotal = BigInt(Math.round(qty)) * unitPrice;
                return (
                  <tr key={l.movementLineId} style={{ borderBottom: '1px solid #ddd' }}>
                    <td
                      style={{ padding: '6px 8px', border: '1px solid #bbb', textAlign: 'center' }}
                    >
                      {i + 1}
                    </td>
                    <td
                      className="ltr sk-num"
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #bbb',
                        fontFamily: 'var(--sk-font-mono)',
                        fontWeight: 600,
                      }}
                    >
                      {l.productCode}
                    </td>
                    <td style={{ padding: '6px 8px', border: '1px solid #bbb' }}>
                      {l.productName}
                    </td>
                    <td
                      style={{ padding: '6px 8px', border: '1px solid #bbb', textAlign: 'center' }}
                    >
                      {l.uomName ?? l.uomCode ?? '—'}
                    </td>
                    <td
                      className="sk-num"
                      style={{
                        padding: '6px 8px',
                        border: '1px solid #bbb',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {qty.toLocaleString('fa-IR')}
                    </td>
                    <td
                      className="ltr sk-num"
                      style={{ padding: '6px 8px', border: '1px solid #bbb', textAlign: 'left' }}
                    >
                      {unitPrice > 0n ? formatRialFa(unitPrice.toString(), unit) : '—'}
                    </td>
                    <td
                      className="ltr sk-num"
                      style={{ padding: '6px 8px', border: '1px solid #bbb', textAlign: 'left' }}
                    >
                      {lineTotal > 0n ? formatRialFa(lineTotal.toString(), unit) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', border: '1px solid #bbb', fontSize: 10 }}>
                      {l.lotCode ? `بچ: ${l.lotCode}` : ''}
                      {l.serialNumber ? ` سریال: ${l.serialNumber}` : ''}
                      {!l.lotCode && !l.serialNumber ? '—' : ''}
                    </td>
                    <td style={{ padding: '6px 8px', border: '1px solid #bbb', fontSize: 10 }}>
                      {l.locationName ?? l.locationCode ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 700 }}>
                <td
                  colSpan={4}
                  style={{ padding: '8px', border: '1px solid #bbb', textAlign: 'right' }}
                >
                  جمع کل
                </td>
                <td
                  className="sk-num"
                  style={{ padding: '8px', border: '1px solid #bbb', textAlign: 'center' }}
                >
                  {totalQty.toLocaleString('fa-IR')}
                </td>
                <td style={{ padding: '8px', border: '1px solid #bbb' }}></td>
                <td
                  className="ltr sk-num"
                  style={{ padding: '8px', border: '1px solid #bbb', textAlign: 'left' }}
                >
                  {totalValue > 0n ? formatRialFa(totalValue.toString(), unit) : '—'}
                </td>
                <td colSpan={2} style={{ padding: '8px', border: '1px solid #bbb' }}></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* 4 Official Authorized Signature Boxes */}
        <div
          style={{
            marginTop: 20,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
          }}
        >
          {doc.movementType === 'receipt' ? (
            <>
              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>
                  تحویل‌دهنده (تأمین‌کننده / راننده)
                </span>
                <span style={{ fontSize: 10, color: '#666' }}>تحویل فیزیکی اقلام</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و تاریخ</span>
              </div>

              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>انباردار تحویل‌گیرنده</span>
                <span style={{ fontSize: 10, color: '#666' }}>شمارش و تطبیق با بارنامه</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و مهر انبار</span>
              </div>

              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>کنترل کیفیت (QC)</span>
                <span style={{ fontSize: 10, color: '#666' }}>تأیید انطباق فنی و سلامت بار</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و تاریخ</span>
              </div>

              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>مسئول حسابداری انبار</span>
                <span style={{ fontSize: 10, color: '#666' }}>ثبت در کاردکس و سند مالی</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و تاریخ</span>
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>انباردار صادرکننده</span>
                <span style={{ fontSize: 10, color: '#666' }}>جمع‌آوری و صدور حواله</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و مهر انبار</span>
              </div>

              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>کنترل انتظامات / بازرسی خروج</span>
                <span style={{ fontSize: 10, color: '#666' }}>تطبیق بار فیزیکی با برگه خروج</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و ساعت خروج</span>
              </div>

              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>راننده / متصدی حمل</span>
                <span style={{ fontSize: 10, color: '#666' }}>تعهد تحویل سالم بار به مقصد</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و شماره پلاک</span>
              </div>

              <div
                style={{
                  border: '1px solid #aaa',
                  borderRadius: 6,
                  padding: '10px 8px',
                  minHeight: 90,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 11 }}>تحویل‌گیرنده نهایی (مشتری)</span>
                <span style={{ fontSize: 10, color: '#666' }}>وصول قطعی و رضایت از اقلام</span>
                <div style={{ height: 26 }}></div>
                <span style={{ fontSize: 10, color: '#888' }}>امضا و اثر انگشت</span>
              </div>
            </>
          )}
        </div>
      </div>
    </PrintPageShell>
  );
}
